import React, { useState, useRef, useCallback } from 'react';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { X, Columns2, Lock } from 'lucide-react';

// Renders two workspace resources side-by-side. Each pane is an iframe loading
// the app's own route in "chromeless" mode (no sidebar/header). The LEFT pane
// is the working pane; the RIGHT pane is a read-only reference. A draggable
// divider resizes the split. Invoked by dragging one workspace tab onto another.

function chromeless(path, isRef) {
  const sep = path.includes('?') ? '&' : '?';
  return `${path}${sep}chromeless=1${isRef ? '&ref=1' : ''}`;
}

export default function SplitView() {
  const ws = useWorkspace();
  const [leftPct, setLeftPct] = useState(50);
  const dragging = useRef(false);
  const containerRef = useRef(null);

  const onDividerDown = useCallback((e) => { dragging.current = true; e.preventDefault(); }, []);
  const onMove = useCallback((e) => {
    if (!dragging.current || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const pct = ((e.clientX - rect.left) / rect.width) * 100;
    setLeftPct(Math.max(25, Math.min(75, pct)));
  }, []);
  const onUp = useCallback(() => { dragging.current = false; }, []);

  React.useEffect(() => {
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [onMove, onUp]);

  if (!ws?.split) return null;
  const { leftPath, leftTitle, rightPath, rightTitle } = ws.split;

  return (
    <div ref={containerRef} className="flex h-full w-full select-none" data-testid="split-view">
      {/* Left / working pane */}
      <div className="flex flex-col min-w-0 border-r border-border/50" style={{ width: `${leftPct}%` }}>
        <div className="flex items-center justify-between px-3 h-8 shrink-0 border-b border-border/40 bg-muted/20">
          <span className="text-[11px] font-medium text-foreground truncate" data-testid="split-left-title">{leftTitle}</span>
          <button onClick={() => ws.clearSplit()} className="p-0.5 rounded text-muted-foreground hover:text-foreground" data-testid="split-close" title="Exit split view"><X className="w-3.5 h-3.5" /></button>
        </div>
        <iframe title={leftTitle} src={chromeless(leftPath, false)} className="flex-1 w-full border-0 bg-background" data-testid="split-left-frame" />
      </div>

      {/* Divider */}
      <div onMouseDown={onDividerDown} className="w-1.5 shrink-0 cursor-col-resize bg-border/40 hover:bg-primary/40 transition-colors" data-testid="split-divider" />

      {/* Right / reference pane */}
      <div className="flex flex-col min-w-0" style={{ width: `${100 - leftPct}%` }}>
        <div className="flex items-center justify-between px-3 h-8 shrink-0 border-b border-border/40 bg-muted/20">
          <span className="text-[11px] font-medium text-foreground truncate flex items-center gap-1.5" data-testid="split-right-title">
            {rightTitle}
            <span className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-wide px-1 py-0.5 rounded bg-amber-500/15 text-amber-400" title="Reference pane — open in its own tab to edit"><Lock className="w-2.5 h-2.5" /> Reference</span>
          </span>
          <Columns2 className="w-3.5 h-3.5 text-muted-foreground/70" />
        </div>
        <iframe title={rightTitle} src={chromeless(rightPath, true)} className="flex-1 w-full border-0 bg-background" data-testid="split-right-frame" />
      </div>
    </div>
  );
}
